import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getFieldTasks, updateFieldTask, deleteFieldTask, getMasterData, getStaffMembers } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertTriangle,
  ClipboardList,
  Trash2,
  Search,
  Filter,
  Building,
  Users,
  Calendar
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { useScreenState, useScrollRestoration } from '../hooks/useScreenState';

// Helper functions
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'In Progress':
      return 'bg-blue-100 text-blue-800';
    case 'Pending':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const isOverdue = (dueDate, status) => {
  if (status === 'Completed') return false;
  const today = new Date().toISOString().split('T')[0];
  return dueDate < today;
};

// TaskCard component defined outside main component
function TaskCard({ task, onStatusChange, onDelete }) {
  return (
    <div
      className={`p-3 rounded-lg border ${
        isOverdue(task.due_date, task.status)
          ? 'border-red-200 bg-red-50'
          : task.status === 'Completed'
          ? 'border-green-200 bg-green-50'
          : 'border-gray-200 bg-white'
      }`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${task.status === 'Completed' ? 'line-through text-gray-500' : ''}`}>
            {task.description}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Building className="h-3 w-3" />
              {task.buyer_name}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Badge className={`text-xs ${getStatusColor(task.status)}`}>
              {task.status}
            </Badge>
            <span className={`text-xs flex items-center gap-1 ${
              isOverdue(task.due_date, task.status) ? 'text-red-600 font-medium' : 'text-gray-500'
            }`}>
              {isOverdue(task.due_date, task.status) ? (
                <AlertTriangle className="h-3 w-3" />
              ) : (
                <Calendar className="h-3 w-3" />
              )}
              {formatDate(task.due_date)}
            </span>
            {task.assigned_names?.length > 0 && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Users className="h-3 w-3" />
                {task.assigned_names.join(', ')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {task.status !== 'Completed' && (
            <>
              {task.status === 'Pending' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => onStatusChange(task.id, 'In Progress')}
                  title="Mark as in progress"
                >
                  <Clock className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={() => onStatusChange(task.id, 'Completed')}
                title="Mark as completed"
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete(task.id)}
            title="Delete task"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {task.visit_id && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <Link
            to={`/field-activity/visit/${task.visit_id}`}
            className="text-xs text-maroon-600 hover:underline"
          >
            View linked visit
          </Link>
        </div>
      )}
    </div>
  );
}

export default function FieldTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [staff, setStaff] = useState([]);

  // Filters
  const [statusFilter, setStatusFilter] = useScreenState('fieldTasks.statusFilter', '');
  const [buyerFilter, setBuyerFilter] = useScreenState('fieldTasks.buyerFilter', '');
  const [assigneeFilter, setAssigneeFilter] = useScreenState('fieldTasks.assigneeFilter', '');
  const [searchQuery, setSearchQuery] = useScreenState('fieldTasks.searchQuery', '');

  useScrollRestoration('fieldTasks', !loading);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (buyerFilter) filters.buyer_id = buyerFilter;
      if (assigneeFilter) filters.assigned_to = assigneeFilter;
      
      const res = await getFieldTasks(filters);
      setTasks(res.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, buyerFilter, assigneeFilter]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [buyersRes, staffRes] = await Promise.all([
          getMasterData('buyer'),
          getStaffMembers(true)
        ]);
        setBuyers(buyersRes.data);
        setStaff(staffRes.data);
      } catch (error) {
        console.error('Failed to load filter data');
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateFieldTask(taskId, { status: newStatus });
      toast.success('Task status updated');
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteFieldTask(taskId);
      toast.success('Task deleted');
      loadTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
    setDeleteTaskId(null);
  };

  // Filter tasks by search query
  const filteredTasks = tasks.filter(task => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.description?.toLowerCase().includes(query) ||
      task.buyer_name?.toLowerCase().includes(query) ||
      task.assigned_names?.some(name => name.toLowerCase().includes(query))
    );
  });

  // Group tasks
  const overdueTasks = filteredTasks.filter(t => isOverdue(t.due_date, t.status));
  const pendingTasks = filteredTasks.filter(t => t.status === 'Pending' && !isOverdue(t.due_date, t.status));
  const inProgressTasks = filteredTasks.filter(t => t.status === 'In Progress' && !isOverdue(t.due_date, t.status));
  const completedTasks = filteredTasks.filter(t => t.status === 'Completed');

  const clearFilters = () => {
    setStatusFilter('');
    setBuyerFilter('');
    setAssigneeFilter('');
    setSearchQuery('');
  };

  const hasFilters = statusFilter || buyerFilter || assigneeFilter || searchQuery;

  return (
    <div className="space-y-4" data-testid="field-tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/field-activity')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-maroon-900">All Tasks</h1>
            <p className="text-sm text-gray-500">{filteredTasks.length} tasks</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 lg:p-2">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-tasks-input"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter || 'ALL_STATUS'}
              onValueChange={(value) => setStatusFilter(value === 'ALL_STATUS' ? '' : value)}
            >
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL_STATUS">All Status</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            {/* Buyer Filter */}
            <MasterDataAutocomplete
              value={buyerFilter}
              onValueChange={setBuyerFilter}
              options={[
                { value: '', label: 'All Buyers', pinned: true },
                ...buyers.map((buyer) => ({ value: buyer.id, label: buyer.name })),
              ]}
              placeholder="All Buyers"
              searchPlaceholder="Search buyers..."
              className="w-full sm:w-40"
              testId="buyer-filter"
            />

            {/* Assignee Filter */}
            <MasterDataAutocomplete
              value={assigneeFilter}
              onValueChange={setAssigneeFilter}
              options={[
                { value: '', label: 'All Assignees', pinned: true },
                ...staff.map((member) => ({ value: member.id, label: member.name })),
              ]}
              placeholder="All Assignees"
              searchPlaceholder="Search employees..."
              className="w-full sm:w-40"
              testId="assignee-filter"
            />

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <Filter className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon-600"></div>
        </div>
      ) : filteredTasks.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">
              {hasFilters ? 'No tasks match your filters' : 'No tasks found'}
            </p>
            {hasFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2 text-maroon-600">
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Overdue Tasks */}
          {overdueTasks.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2 bg-red-50 rounded-t-lg">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2 text-red-800">
                  <AlertTriangle className="h-4 w-4" />
                  Overdue ({overdueTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2">
                {overdueTasks.map((task) => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onStatusChange={handleTaskStatusChange}
                    onDelete={setDeleteTaskId}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Pending Tasks */}
          {pendingTasks.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2 text-yellow-800">
                  <Clock className="h-4 w-4" />
                  Pending ({pendingTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2">
                {pendingTasks.map((task) => (
                  <TaskCard 
                    key={task.id} 
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    onDelete={setDeleteTaskId}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* In Progress Tasks */}
          {inProgressTasks.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2 text-blue-800">
                  <Clock className="h-4 w-4" />
                  In Progress ({inProgressTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2">
                {inProgressTasks.map((task) => (
                  <TaskCard 
                    key={task.id} 
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    onDelete={setDeleteTaskId}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Completed Tasks */}
          {completedTasks.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2 text-green-800">
                  <CheckCircle className="h-4 w-4" />
                  Completed ({completedTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2">
                {completedTasks.map((task) => (
                  <TaskCard 
                    key={task.id} 
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    onDelete={setDeleteTaskId}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Delete Task Dialog */}
      <AlertDialog open={!!deleteTaskId} onOpenChange={() => setDeleteTaskId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTask(deleteTaskId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
